import { useEffect, useState } from "react";
import styled from "styled-components";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
    addControlDeliveriesAction,
    addControlDeliveryAction,
    deliveryState,
    removeControlDeliveriesByIdsAction,
    removeControlDeliveryByIdAction,
} from "../../app/reducers/delivery-slice";
import theme from "../../style/theme";
import { ControlDelivery } from "../../types/delivery/control-delivery";
import { Delivery } from "../../types/delivery/delivery";
import { DeliveryStatus } from "../../types/delivery/delivery-status";
import { DeliveryGroupType } from "../../types/schedule/delivery-group-type";
import { timeFormat } from "../../utils/date/date-format";
import { SmallBadgeCard } from "../card/SmallBadgeCard";
import ArrowRightFillIcon from "../icons/ArrowRightFillIcon";
import BlueCheckFillIcon from "../icons/BlueCheckFillIcon/BlueCheckFillIcon";
import PlusFillIcon from "../icons/PlusFillIcon";
import YellowCheckFillIcon from "../icons/YellowCheckFillIcon/YellowCheckFillIcon";

export const DisplayDeliveryDrawer = () => {
    const dispatch = useAppDispatch();
    const { selectedDisplayDeliveryId, displayDeliveries, controlDeliveries } =
        useAppSelector(deliveryState);

    const [allDisabled, setAllDisabled] = useState(false);
    const [checkedAllDeliveryCount, setCheckedAllDeliveryCount] = useState(0);
    const [totalAllDeliveryCount, setTotalAllDeliveryCount] = useState(0);

    const selectedDisplayDelivery = displayDeliveries.find(
        (displayDelivery) => displayDelivery.id === selectedDisplayDeliveryId
    );

    useEffect(() => {
        if (selectedDisplayDelivery) {
            const isAllDisabled = selectedDisplayDelivery.deliveries.every(
                (selectedDelivery) =>
                    controlDeliveries.some(
                        (controlDelivery: ControlDelivery) =>
                            controlDelivery.delivery.id === selectedDelivery.id
                    )
            );
            setAllDisabled(isAllDisabled);
            const checkedAllDeliveryCount = selectedDisplayDelivery.deliveries.filter(
                (delivery) =>
                    controlDeliveries.some(
                        (controlDelivery) => controlDelivery.delivery.id === delivery.id
                    )
            ).length;
            setCheckedAllDeliveryCount(checkedAllDeliveryCount);
            setTotalAllDeliveryCount(selectedDisplayDelivery.deliveries.length);
        }
    }, [controlDeliveries, selectedDisplayDelivery]);

    if (!selectedDisplayDelivery) {
        return (
            <Container>
                <Header>
                    <HeaderLabel>물류 그룹 선택</HeaderLabel>
                </Header>
            </Container>
        );
    }

    const isSelectedDisplayDeliveryPickUp =
        selectedDisplayDelivery.type === DeliveryGroupType.PickUp;

    const isSelectedDisplayDeliveryDropOff =
        selectedDisplayDelivery.type === DeliveryGroupType.DropOff;

    const convertSelectedDisplayDeliveryTypeToText = () => {
        switch (selectedDisplayDelivery.type) {
            case DeliveryGroupType.PickUp:
                return "상차";
            default:
                return "전달";
        }
    };

    const convertSelectedDisplayDeliveryItemTypeToText = () => {
        switch (selectedDisplayDelivery.type) {
            case DeliveryGroupType.PickUp:
                return "전달";
            default:
                return "상차";
        }
    };

    const getSelectedDeliveryAddress = (delivery: Delivery) => {
        if (isSelectedDisplayDeliveryPickUp) {
            const { detail, alias } = delivery.pickUpWorkPlace;
            return `${alias} ${detail}`;
        }
        const { detail, alias } = delivery.dropOffWorkPlace;
        return `${alias} ${detail}`;
    };

    const getSelectedDeliveryItemAddress = (delivery: Delivery) => {
        if (isSelectedDisplayDeliveryDropOff) {
            const { detail, alias } = delivery.pickUpWorkPlace;
            return `${alias} ${detail}`;
        }
        const { detail, alias } = delivery.dropOffWorkPlace;
        return `${alias} ${detail}`;
    };

    const selectedDeliveriesMap: { [key: string]: Delivery[] } =
        selectedDisplayDelivery.deliveries.reduce((acc: any, delivery) => {
            acc[getSelectedDeliveryAddress(delivery)] =
                acc[getSelectedDeliveryAddress(delivery)] || [];
            acc[getSelectedDeliveryAddress(delivery)].push(delivery);
            return acc;
        }, {});

    const addAllDelivery = () => {
        const filteredDisabled = selectedDisplayDelivery.deliveries.filter(
            (delivery) => {
                if (isSelectedDisplayDeliveryPickUp) {
                    return !delivery.pickUpDisabled;
                }
                return !delivery.dropOffDisabled;
            }
        );

        const deliveries = filteredDisabled.map((delivery) => {
            return {
                key: delivery.id + selectedDisplayDelivery.type,
                delivery,
                type: selectedDisplayDelivery.type,
            };
        });

        dispatch(
            addControlDeliveriesAction({
                deliveries,
            })
        );
    };

    const removeAllDelivery = () => {
        const filteredDisabled = selectedDisplayDelivery.deliveries.filter(
            (delivery) => {
                if (isSelectedDisplayDeliveryPickUp) {
                    return !delivery.pickUpDisabled;
                }
                return !delivery.dropOffDisabled;
            }
        );

        dispatch(
            removeControlDeliveriesByIdsAction({
                deliveryIds: filteredDisabled.map((delivery) => delivery.id),
            })
        );
    };

    const removeAllDeliveryInDeliveryGroupCards = (
        selectedDeliveries: Delivery[]
    ) => {
        const filteredDisabled = selectedDeliveries.filter((delivery) => {
            if (isSelectedDisplayDeliveryPickUp) {
                return !delivery.pickUpDisabled;
            }
            return !delivery.dropOffDisabled;
        });

        dispatch(
            removeControlDeliveriesByIdsAction({
                deliveryIds: filteredDisabled.map((delivery) => delivery.id),
            })
        );
    };

    const addAllDeliveryInDeliveryGroupCards = (
        selectedDeliveries: Delivery[]
    ) => {
        const filteredDisabled = selectedDeliveries.filter((delivery) => {
            if (isSelectedDisplayDeliveryPickUp) {
                return !delivery.pickUpDisabled;
            }
            return !delivery.dropOffDisabled;
        });

        const deliveries = filteredDisabled.map((delivery) => {
            return {
                key: delivery.id + selectedDisplayDelivery.type,
                delivery,
                type: selectedDisplayDelivery.type,
            };
        });

        dispatch(
            addControlDeliveriesAction({
                deliveries,
            })
        );
    };

    const isIncludeDeliveryInControlDeliveries = (deliveryId: string) => {
        return controlDeliveries
            .map((controlDelivery) => controlDelivery.delivery.id)
            .includes(deliveryId);
    };

    return (
        <Container>
            <Header>
                <LeftHeaderLabel>
                    {allDisabled ? (
                        <IconContainer disabled={false} onClick={removeAllDelivery}>
                            <YellowCheckFillIcon style={{ width: "19px", height: "19px" }} />
                        </IconContainer>
                    ) : (
                        <IconContainer disabled={false} onClick={addAllDelivery}>
                            <PlusFillIcon style={{ width: "19px", height: "19px" }} />
                        </IconContainer>
                    )}
                    <HeaderLabel>물류 그룹 선택</HeaderLabel>
                </LeftHeaderLabel>
                <RightHeaderLabel>
                    총 {checkedAllDeliveryCount}/{totalAllDeliveryCount}건
                </RightHeaderLabel>
            </Header>
            {Object.values(selectedDeliveriesMap).map((selectedDeliveries, index) => {
                const allPickUpDisabled = selectedDeliveries.every(
                    (selectedDelivery) => selectedDelivery.pickUpDisabled
                );
                const totalDisplayDeliveryCount = selectedDeliveries.length;
                const checkedDisplayDeliveryCount = selectedDeliveries.filter(
                    (delivery) =>
                        controlDeliveries.some(
                            (controlDelivery) => controlDelivery.delivery.id === delivery.id
                        )
                ).length;
                return (
                    <>
                        <ContentTitleContainer isPickUp={isSelectedDisplayDeliveryPickUp}>
                            <ContentTitleFlexTopContainer>
                                {totalDisplayDeliveryCount === checkedDisplayDeliveryCount ? (
                                    <IconContainer
                                        disabled={allPickUpDisabled}
                                        onClick={() =>
                                            removeAllDeliveryInDeliveryGroupCards(selectedDeliveries)
                                        }
                                    >
                                        {isSelectedDisplayDeliveryPickUp && (
                                            <BlueCheckFillIcon
                                                style={{ width: "19px", height: "19px" }}
                                            />
                                        )}
                                        {isSelectedDisplayDeliveryDropOff && (
                                            <YellowCheckFillIcon
                                                style={{ width: "19px", height: "19px" }}
                                            />
                                        )}
                                    </IconContainer>
                                ) : (
                                    <IconContainer
                                        disabled={allPickUpDisabled}
                                        onClick={() =>
                                            addAllDeliveryInDeliveryGroupCards(selectedDeliveries)
                                        }
                                    >
                                        <PlusFillIcon style={{ width: "19px", height: "19px" }} />
                                    </IconContainer>
                                )}
                                <ContentTitleLeftLabel>
                                    {convertSelectedDisplayDeliveryTypeToText()}
                                </ContentTitleLeftLabel>
                                <ContentTitleLeftAddressLabel
                                    title={getSelectedDeliveryAddress(selectedDeliveries[0])}
                                >
                                    {getSelectedDeliveryAddress(selectedDeliveries[0])}
                                </ContentTitleLeftAddressLabel>
                            </ContentTitleFlexTopContainer>
                            <ContentTitleFlexBottomContainer>
                                <ContentTitleRightLabel>
                                    총 {checkedDisplayDeliveryCount}/{totalDisplayDeliveryCount}건
                                </ContentTitleRightLabel>
                            </ContentTitleFlexBottomContainer>
                        </ContentTitleContainer>
                        <ContentContainer>
                            {selectedDeliveries.map((delivery) => {
                                const disabled = isSelectedDisplayDeliveryPickUp
                                    ? delivery.dropOffDisabled
                                    : delivery.pickUpDisabled;
                                const isMatched = delivery.status !== DeliveryStatus.Wait;
                                const loadLabel =
                                    (delivery.load.name === "null" || delivery.load.name === ""
                                        ? "물품 현장 확인"
                                        : delivery.load.name) +
                                    (delivery.load.quantity ? ` ${delivery.load.quantity}` : '');

                                return (
                                    <ContentItemContainer
                                        isInclude={isIncludeDeliveryInControlDeliveries(
                                            delivery.id
                                        )}
                                        isPickUp={isSelectedDisplayDeliveryPickUp}
                                        disabled={disabled}
                                    >
                                        <ItemTopContainer>
                                            {isIncludeDeliveryInControlDeliveries(delivery.id) ? (
                                                <IconContainer
                                                    disabled={delivery.pickUpDisabled}
                                                    onClick={() =>
                                                        dispatch(
                                                            removeControlDeliveryByIdAction({
                                                                deliveryId: delivery.id,
                                                            })
                                                        )
                                                    }
                                                >
                                                    {isSelectedDisplayDeliveryPickUp ? (
                                                        <YellowCheckFillIcon />
                                                    ) : (
                                                        <BlueCheckFillIcon />
                                                    )}
                                                </IconContainer>
                                            ) : (
                                                <IconContainer
                                                    disabled={delivery.pickUpDisabled}
                                                    onClick={() =>
                                                        dispatch(
                                                            addControlDeliveryAction({
                                                                delivery: {
                                                                    key:
                                                                        delivery.id + selectedDisplayDelivery.type,
                                                                    delivery,
                                                                    type: selectedDisplayDelivery.type,
                                                                },
                                                            })
                                                        )
                                                    }
                                                >
                                                    <PlusFillIcon />
                                                </IconContainer>
                                            )}
                                            <ContentItemLabelContainer>
                                                <ContentItemLabel>
                                                    {convertSelectedDisplayDeliveryItemTypeToText()}
                                                </ContentItemLabel>
                                            </ContentItemLabelContainer>
                                            <ContentItemDeliveryAddressLabel
                                                title={getSelectedDeliveryItemAddress(delivery)}
                                            >
                                                {getSelectedDeliveryItemAddress(delivery)}
                                            </ContentItemDeliveryAddressLabel>
                                        </ItemTopContainer>
                                        <ItemBottomContainer>
                                            <ContentItemLabel>
                                                {timeFormat(delivery.pickUpAction.startTimeLimited)}
                                            </ContentItemLabel>
                                            <ArrowRightFillIconContainer>
                                                <ArrowRightFillIcon
                                                    width="10px"
                                                    height="10px"
                                                    fill={theme.palette.primitives.gray300}
                                                />
                                            </ArrowRightFillIconContainer>
                                            <ContentItemLabel>
                                                {timeFormat(delivery.dropOffAction.startTimeLimited)}
                                            </ContentItemLabel>
                                            <LoadLabelContainer title={loadLabel}>
                                                {loadLabel}
                                            </LoadLabelContainer>
                                            {isMatched && (
                                                <SmallBadgeCard label="배정완료" color="red" />
                                            )}
                                        </ItemBottomContainer>
                                    </ContentItemContainer>
                                );
                            })}
                        </ContentContainer>
                    </>
                );
            })}
        </Container>
    );
};

const Container = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  z-index: 99999;
  background: ${theme.palette.primitives.white};
  flex: 1;
  overflow-y: scroll;
`;

const Header = styled.div`
  padding: 12px 12px;
  border-bottom: 1px solid ${theme.palette.primitives.gray200};
  display: flex;
  align-items: center;
  flex-direction: row;
  justify-content: space-between;
`;

const LeftHeaderLabel = styled.div`
  display: flex;
  flex-direction: row;
  vertical-align: middle;
  align-items: center;
`;

const RightHeaderLabel = styled.div`
  height: 18px;
  color: #979da8;
  text-align: right;
  font-size: 15px;
  font-weight: 500;
  line-height: normal;
`;

const HeaderLabel = styled.div`
  font-weight: 500;
  font-size: 18px;
  color: ${theme.palette.primitives.gray800};
  margin: 0px 12px;
`;

interface ContentTitleContainerProps {
    isPickUp: boolean;
}

const ContentTitleContainer = styled.div<ContentTitleContainerProps>`
  display: flex;
  flex-direction: column;
  padding: 10px 12px;
  background: ${(props) => {
        if (props.isPickUp) {
            return theme.palette.primitives.blue100;
        }
        return theme.palette.primitives.yellow100;
    }};
  border-bottom: 1px solid ${theme.palette.primitives.gray100};
`;

const ContentTitleFlexTopContainer = styled.div`
  display: flex;
`;

const ContentTitleFlexBottomContainer = styled.div`
  display: flex;
  align-items: center;
  padding-left: 32px;
`;

const ContentTitleLeftLabel = styled.div`
  padding: 0 17px 0 12px;
  font-weight: 500;
  font-size: 16px;
  line-height: 22px;
  color: ${theme.palette.primitives.gray900};
`;

const ContentTitleLeftAddressLabel = styled.div`
  max-width: 240px;
  font-weight: 500;
  font-size: 16px;
  line-height: 22px;
  color: ${theme.palette.primitives.gray900};

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ContentTitleRightLabel = styled.div`
  font-weight: 400;
  font-size: 14px;
  line-height: 17px;
  color: ${theme.palette.primitives.gray400};
  margin-right: 10px;
`;

const ContentContainer = styled.div`
  display: flex;
  margin-bottom: 8px;
  flex-direction: column;
`;

interface ContentItemContainerProps {
    isInclude: boolean;
    isPickUp: boolean;
    disabled: boolean;
}

const ContentItemContainer = styled.div<ContentItemContainerProps>`
  display: flex;
  flex-direction: column;
  padding: 10px 12px;
  background: ${(props) => {
        if (props.disabled) {
            return theme.palette.primitives.gray50;
        }
        if (props.isInclude) {
            if (props.isPickUp) {
                return theme.palette.primitives.yellow50;
            }
            return theme.palette.primitives.blue50;
        }
    }};
  border-bottom: 1px solid ${theme.palette.primitives.gray100};
`;

const ItemTopContainer = styled.div`
  display: flex;
`;

const ItemBottomContainer = styled.div`
  display: flex;
  padding-left: 29px;
  align-items: center;
`;

const ContentItemLabelContainer = styled.div`
  padding: 0px 23px 0px 12px;
`;

const ContentItemLabel = styled.div`
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 17px;
  color: ${theme.palette.primitives.gray600};
`;

const ContentItemDeliveryAddressLabel = styled.div`
  max-width: 280px;
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 17px;
  color: ${theme.palette.primitives.gray900};

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ArrowRightFillIconContainer = styled.div`
  margin: 0 2px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const LoadLabelContainer = styled.div`
  max-width: 280px;
  margin: 0 10px;

  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 17px;
  color: ${theme.palette.primitives.gray600};

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

interface IconContainerProps {
    disabled: boolean;
}

const IconContainer = styled.div<IconContainerProps>`
  cursor: pointer;
  visibility: ${(props) => (props.disabled ? "hidden" : "visible")};
`;
